import { ApiError } from "@kkp/api-client";
import { z } from "zod";

/**
 * Validate a list response at the API boundary. Accepts either an `{ items: [] }`
 * envelope or a bare array, parses every row against `schema`, and throws an
 * `ApiError` on a shape mismatch — replacing the blind `as unknown as` casts
 * that let malformed money / PII reach the UI unchecked. Money fields are typed
 * `z.string()`: the BFFE serializes MoneyIdr as a string and the FE keeps it as
 * an opaque string end-to-end (parsed only at the display formatter).
 */
export function parseListItems<S extends z.ZodTypeAny>(
  schema: S,
  data: unknown,
  resource: string,
): z.infer<S>[] {
  const raw =
    data &&
    typeof data === "object" &&
    Array.isArray((data as { items?: unknown }).items)
      ? (data as { items: unknown[] }).items
      : data;

  const result = z.array(schema).safeParse(raw);
  if (!result.success) {
    throw new ApiError({
      message: `Received a malformed ${resource} response from the server.`,
      httpStatus: 502,
    });
  }
  return result.data;
}

/**
 * Single-object analogue of {@link parseListItems}. Parses one response object
 * against `schema` and throws an `ApiError` 502 on a shape mismatch, instead of
 * letting a blind cast leak malformed money / PII into the UI.
 */
export function parseObject<S extends z.ZodTypeAny>(
  schema: S,
  data: unknown,
  resource: string,
): z.infer<S> {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new ApiError({
      message: `Received a malformed ${resource} response from the server.`,
      httpStatus: 502,
    });
  }
  return result.data;
}

/**
 * A 64-bit wire integer, kept as its decimal string (JS numbers lose digits past
 * 2^53). A JSON integer is accepted too, so the SPA can ship before a BFFE that
 * still sends numbers is replaced.
 */
export const wireInt = z
  .union([z.string().regex(/^-?\d+$/), z.number().int()])
  .transform(String);
